import { Maybe, MutationData, rd, RemoteData } from '@passionware/monads';
import { useSimpleEventSubscription } from '@passionware/simple-event';
import { SimpleStore, useSimpleStore } from '@passionware/simple-store';
import { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContentHeadless } from 'v5/design-system/Dialog';
import { Form } from '../../../design-system/Form';
import { AtellioFeatureGroupDefinition } from '../../../features';
import { FormDialogContent } from 'v5/patterns/forms/dialogs/FormDialogContent';
import { FeatureFlagValues } from '../../../platform/feature-management/feature-management';
import {
  FieldDefinition,
  FieldFormModel,
  formFieldModel
} from './field-definition';
import { CustomFieldConfigFormContent } from './CustomFieldConfigFormContent';
import { getFields } from './fields';

export interface CustomFieldConfigDialogProps {
  features: Partial<FeatureFlagValues<AtellioFeatureGroupDefinition>>;
  /**
   * Simple store - because we want some reactivity to initial value
   * RemoteData - because we want to understand that default data is being loaded, and we can show a skeleton or loading error
   * Maybe - to support creating empty field from scratch
   * FieldDefinition - because we want to work with field definition to initialize the form
   */
  resetStore: SimpleStore<RemoteData<Maybe<FieldDefinition>>>;
  onSubmit: (data: Maybe<FieldDefinition>) => void;
  onCancel: () => void;
  open: boolean;
  mode: 'create' | 'edit' | null;
  mutation: MutationData<unknown, unknown>;
  prompt: ReactNode;
  disabledFields?: (keyof FieldFormModel)[];
  hideDisabledFieldTypeOptions?: boolean;
}

export function CustomFieldConfigDialog(props: CustomFieldConfigDialogProps) {
  const initialField = useSimpleStore(props.resetStore);
  const form = useForm({
    defaultValues: rd.tryMap(initialField, formFieldModel.fromDefinition),
    mode: 'onChange',
    reValidateMode: 'onChange'
  });

  useSimpleEventSubscription(props.resetStore.addUpdateListener, value =>
    rd.map(value, value => form.reset(formFieldModel.fromDefinition(value)))
  );

  const handleSubmit = (data: FieldFormModel) => {
    props.onSubmit(formFieldModel.toDefinition(data));
  };

  const fields = getFields(props.features);

  const title =
    props.mode && { create: 'Create field', edit: 'Edit field' }[props.mode];

  const isFormValid = form.formState.isValid;
  const isFormDirty = form.formState.isDirty;

  const isSaveDisabled = !isFormValid || !isFormDirty;

  return (
    <Form {...form}>
      <Dialog
        open={props.open}
        onOpenChange={open => {
          if (!open) {
            props.onCancel();
          }
        }}
      >
        <DialogContentHeadless>
          <FormDialogContent
            disabled={isSaveDisabled}
            onSubmit={form.handleSubmit(handleSubmit)}
            open={props.open}
            title={title}
            mutation={props.mutation}
            description="Custom field configuration"
          >
            <CustomFieldConfigFormContent
              initialField={initialField}
              form={form}
              fields={fields}
              features={props.features}
              disabledFields={props.disabledFields}
              hideDisabledFieldTypeOptions={props.hideDisabledFieldTypeOptions}
              prompt={props.prompt}
            />
          </FormDialogContent>
        </DialogContentHeadless>
      </Dialog>
    </Form>
  );
}
